import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Grid,
  LinearProgress,
  Autocomplete,
  MenuItem,
  Switch,
  Divider,
  Tooltip,
  Tab,
  Tabs,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  Edit as EditIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  EditNote as AdjustIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Science as ScienceIcon,
  Category as CategoryIcon,
  LocalShipping as ShippingIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import {
  useRawMaterials,
  useRawMaterialAlerts,
  useRawMaterialProcurements,
  useProcureRawMaterial,
  useBulkProcureRawMaterials,
  useMarkRawMaterialDelivered,
  useDeleteRawMaterialProcurement,
  useCreateRawMaterial,
  useUpdateRawMaterial,
  useUpdateRawMaterialProcurement,
  useAdjustRawMaterialStock,
  useToggleRawMaterialStatus,
  useRawMaterialHistory,
  useSuppliers,
  useCreateSupplier,
} from '../hooks/useFeed';
import { format } from 'date-fns';

// ─── design tokens ─────────────────────────────────────────────────────────────

const dataGridSx = {
  border: 0,
  background: 'transparent',
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-columnHeaders': {
    background: 'rgba(255,152,0,0.05)',
    borderBottom: '1px solid rgba(255,152,0,0.12)',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.7px',
    color: 'rgba(255,152,0,0.6)',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-row:hover': { background: 'rgba(255,152,0,0.04)' },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid rgba(255,152,0,0.1)',
    background: 'rgba(255,152,0,0.03)',
  },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
    { color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' },
  '& .MuiTablePagination-select': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.15)' },
  '& .MuiDataGrid-virtualScroller': { minHeight: 160 },
};

const dialogPaper = {
  borderRadius: 3,
  background: '#0e0e10',
  backgroundImage: 'none',
  border: '1px solid rgba(255,152,0,0.2)',
};

const DialogHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box
    sx={{
      px: 3,
      pt: 3,
      pb: 2.5,
      borderBottom: '1px solid rgba(255,152,0,0.1)',
      background: 'linear-gradient(135deg, rgba(255,152,0,0.06) 0%, transparent 100%)',
    }}
  >
    <Typography variant="h6" fontWeight={800} sx={{ color: '#ffffff' }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.38)' }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// ─── Page ──────────────────────────────────────────────────────────────────────

const RawMaterialsPage: React.FC = () => {
  const {
    data: materials,
    isLoading: materialsLoading,
    isFetching: isRawMaterialsFetching,
  } = useRawMaterials();
  const { data: alerts } = useRawMaterialAlerts();
  const {
    data: procurements,
    isLoading: procurementsLoading,
    isFetching: isProcurementsFetching,
  } = useRawMaterialProcurements();
  const markDeliveredMutation = useMarkRawMaterialDelivered();
  const deleteProcurementMutation = useDeleteRawMaterialProcurement();
  const updateProcurementMutation = useUpdateRawMaterialProcurement();
  const procureMutation = useProcureRawMaterial();
  const createMutation = useCreateRawMaterial();
  const updateMutation = useUpdateRawMaterial();
  const adjustStockMutation = useAdjustRawMaterialStock();
  const toggleStatusMutation = useToggleRawMaterialStatus();
  const { data: suppliers = [] } = useSuppliers();
  const createSupplierMutation = useCreateSupplier();

  const [tab, setTab] = useState(0);
  const [procureDialogOpen, setProcureDialogOpen] = useState(false);
  const [editProcurementDialogOpen, setEditProcurementDialogOpen] = useState(false);
  const [editingProcurement, setEditingProcurement] = useState<any>(null);
  const [editProcurementForm, setEditProcurementForm] = useState({
    bags_purchased: '',
    kg_per_bag: '',
    cost_per_bag: '',
    is_credit: false,
    supplier_id: null as number | null,
    notes: '',
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [adjustStockDialogOpen, setAdjustStockDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<any>(null);
  const [quickAddSupplierDialogOpen, setQuickAddSupplierDialogOpen] = useState(false);
  const [quickAddSupplierName, setQuickAddSupplierName] = useState('');

  const [stockFilters, setStockFilters] = useState({
    materialName: '',
    stockStatus: '',
    activeStatus: '',
  });
  const [procurementFilters, setProcurementFilters] = useState({ materialName: '', status: '' });

  // ── bulk procurement state ────────────────────────────────────────────────
  const emptyLine = () => ({
    raw_material_id: 0,
    bags_purchased: '',
    cost_per_bag: '',
    kg_per_bag: '50',
    supplier_id: null as number | null,
    is_credit: false,
    notes: '',
  });
  const [procureShared, setProcureShared] = useState({
    procurement_date: format(new Date(), 'yyyy-MM-dd'),
    delivery_date: '',
  });
  const [procureLines, setProcureLines] = useState([emptyLine()]);
  const updateLine = (idx: number, patch: Partial<ReturnType<typeof emptyLine>>) =>
    setProcureLines((lines) => lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const bulkProcureMutation = useBulkProcureRawMaterials();

  // Legacy single-item state kept only to avoid breaking edit-procurement form references
  const [formData, setFormData] = useState({
    raw_material_id: 0,
    bags_purchased: '',
    cost_per_bag: '',
    kg_per_bag: '50',
    procurement_date: format(new Date(), 'yyyy-MM-dd'),
    delivery_date: '',
    supplier_id: null as number | null,
    is_credit: false,
    notes: '',
  });

  const [materialFormData, setMaterialFormData] = useState({
    name: '',
    description: '',
    unit_of_measure: 'kg',
    default_kg_per_bag: '50',
    minimum_threshold: '100',
    is_active: true,
  });

  const [editMaterialFormData, setEditMaterialFormData] = useState({
    name: '',
    description: '',
    unit_of_measure: 'kg',
    default_kg_per_bag: '',
    minimum_threshold: '',
    current_stock: '',
    top_up_kg: '',
    is_active: true,
  });

  const [adjustStockFormData, setAdjustStockFormData] = useState({ new_quantity: '', reason: '' });
  const [historyMaterialId, setHistoryMaterialId] = useState<number | null>(null);
  const [historyMaterialName, setHistoryMaterialName] = useState('');
  const { data: historyData, isLoading: historyLoading } = useRawMaterialHistory(historyMaterialId);

  const isMutating =
    procureMutation.isPending ||
    createMutation.isPending ||
    updateMutation.isPending ||
    updateProcurementMutation.isPending ||
    adjustStockMutation.isPending ||
    toggleStatusMutation.isPending ||
    markDeliveredMutation.isPending ||
    deleteProcurementMutation.isPending ||
    createSupplierMutation.isPending;

  const isFetching = isRawMaterialsFetching || isProcurementsFetching;

  // ── handlers ─────────────────────────────────────────────────────────────

  const handleProcure = async () => {
    try {
      const items = procureLines
        .filter((l) => l.raw_material_id && l.bags_purchased && l.cost_per_bag)
        .map((l) => ({
          raw_material_id: l.raw_material_id,
          bags_purchased: parseFloat(l.bags_purchased),
          cost_per_bag: parseFloat(l.cost_per_bag),
          kg_per_bag: parseFloat(l.kg_per_bag) || 50,
          procurement_date: procureShared.procurement_date,
          delivery_date: procureShared.delivery_date || undefined,
          supplier_id: l.supplier_id || undefined,
          is_credit: l.is_credit,
          notes: l.notes || undefined,
        }));
      await bulkProcureMutation.mutateAsync({ items });
      setProcureDialogOpen(false);
      setProcureLines([emptyLine()]);
      setProcureShared({ procurement_date: format(new Date(), 'yyyy-MM-dd'), delivery_date: '' });
    } catch {
      /* errors handled by mutation */
    }
  };

  const handleCreateMaterial = async () => {
    try {
      await createMutation.mutateAsync({
        name: materialFormData.name,
        description: materialFormData.description || undefined,
        unit_of_measure: materialFormData.unit_of_measure,
        default_kg_per_bag: parseFloat(materialFormData.default_kg_per_bag),
        minimum_threshold: parseFloat(materialFormData.minimum_threshold),
        is_active: materialFormData.is_active,
      });
      setCreateDialogOpen(false);
      setMaterialFormData({
        name: '',
        description: '',
        unit_of_measure: 'kg',
        default_kg_per_bag: '50',
        minimum_threshold: '100',
        is_active: true,
      });
    } catch {}
  };

  const handleEditMaterial = (material: any) => {
    setEditingMaterial(material);
    setEditMaterialFormData({
      name: material.name || '',
      description: material.description || '',
      unit_of_measure: material.unit_of_measure || 'kg',
      default_kg_per_bag: material.default_kg_per_bag?.toString() || '',
      minimum_threshold: material.minimum_threshold?.toString() || '',
      current_stock: material.current_stock?.toString() || '',
      top_up_kg: material.top_up_kg != null ? material.top_up_kg.toString() : '',
      is_active: material.is_active ?? true,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateMaterial = async () => {
    if (!editingMaterial) return;
    try {
      await updateMutation.mutateAsync({
        id: editingMaterial.id,
        data: {
          name: editMaterialFormData.name || undefined,
          description: editMaterialFormData.description || undefined,
          unit_of_measure: editMaterialFormData.unit_of_measure || undefined,
          default_kg_per_bag: editMaterialFormData.default_kg_per_bag
            ? parseFloat(editMaterialFormData.default_kg_per_bag)
            : undefined,
          minimum_threshold: editMaterialFormData.minimum_threshold
            ? parseFloat(editMaterialFormData.minimum_threshold)
            : undefined,
          current_stock: editMaterialFormData.current_stock
            ? parseFloat(editMaterialFormData.current_stock)
            : undefined,
          top_up_kg: editMaterialFormData.top_up_kg
            ? parseFloat(editMaterialFormData.top_up_kg)
            : null,
          is_active: editMaterialFormData.is_active,
        },
      });
      setEditDialogOpen(false);
      setEditingMaterial(null);
    } catch {}
  };

  const handleMarkDelivered = async (id: number) => {
    try {
      await markDeliveredMutation.mutateAsync({
        id,
        delivery_date: format(new Date(), 'yyyy-MM-dd'),
      });
    } catch {}
  };

  const handleDeleteProcurement = async (id: number) => {
    if (!window.confirm('Delete this procurement? Stock will be adjusted if already delivered.'))
      return;
    try {
      await deleteProcurementMutation.mutateAsync(id);
    } catch {}
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    if (!window.confirm(`${currentStatus ? 'Deactivate' : 'Activate'} this material?`)) return;
    try {
      await toggleStatusMutation.mutateAsync(id);
    } catch {}
  };

  const handleAdjustStock = (material: any) => {
    setEditingMaterial(material);
    setAdjustStockFormData({ new_quantity: material.current_stock?.toString() || '0', reason: '' });
    setAdjustStockDialogOpen(true);
  };

  const handleSubmitStockAdjustment = async () => {
    if (!editingMaterial) return;
    try {
      await adjustStockMutation.mutateAsync({
        id: editingMaterial.id,
        data: {
          new_quantity: parseFloat(adjustStockFormData.new_quantity),
          reason: adjustStockFormData.reason || undefined,
        },
      });
      setAdjustStockDialogOpen(false);
      setEditingMaterial(null);
      setAdjustStockFormData({ new_quantity: '', reason: '' });
    } catch {}
  };

  const handleOpenEditProcurement = (procurement: any) => {
    setEditingProcurement(procurement);
    setEditProcurementForm({
      bags_purchased: procurement.bags_purchased?.toString() ?? '',
      kg_per_bag: procurement.kg_per_bag?.toString() ?? '',
      cost_per_bag: procurement.cost_per_bag?.toString() ?? '',
      is_credit: procurement.is_credit ?? false,
      supplier_id: procurement.supplier?.id ?? null,
      notes: procurement.notes ?? '',
    });
    setEditProcurementDialogOpen(true);
  };

  const handleUpdateProcurement = async () => {
    if (!editingProcurement) return;
    try {
      await updateProcurementMutation.mutateAsync({
        id: editingProcurement.id,
        data: {
          bags_purchased: editProcurementForm.bags_purchased
            ? parseFloat(editProcurementForm.bags_purchased)
            : undefined,
          kg_per_bag: editProcurementForm.kg_per_bag
            ? parseFloat(editProcurementForm.kg_per_bag)
            : undefined,
          cost_per_bag: editProcurementForm.cost_per_bag
            ? parseFloat(editProcurementForm.cost_per_bag)
            : undefined,
          is_credit: editProcurementForm.is_credit,
          supplier_id: editProcurementForm.supplier_id || undefined,
          notes: editProcurementForm.notes || undefined,
        },
      });
      setEditProcurementDialogOpen(false);
      setEditingProcurement(null);
    } catch {}
  };

  const handleQuickAddSupplier = async (name: string, onCreated: (s: any) => void) => {
    try {
      const supplier = await createSupplierMutation.mutateAsync({ name });
      onCreated(supplier);
      setQuickAddSupplierDialogOpen(false);
      setQuickAddSupplierName('');
    } catch {}
  };

  // ── filtered data ─────────────────────────────────────────────────────────

  const filteredMaterials = (materials ?? []).filter((m: any) => {
    const matchesName =
      !stockFilters.materialName ||
      m.name?.toLowerCase().includes(stockFilters.materialName.toLowerCase());
    const matchesStock =
      !stockFilters.stockStatus ||
      (stockFilters.stockStatus === 'low' ? m.is_low_stock : !m.is_low_stock);
    const matchesActive =
      !stockFilters.activeStatus ||
      (stockFilters.activeStatus === 'active' ? m.is_active : !m.is_active);
    return matchesName && matchesStock && matchesActive;
  });

  const filteredProcurements = (procurements ?? [])
    .filter((p: any) => {
      const matchesName =
        !procurementFilters.materialName ||
        p.raw_material?.name?.toLowerCase().includes(procurementFilters.materialName.toLowerCase());
      const matchesStatus = !procurementFilters.status || p.status === procurementFilters.status;
      return matchesName && matchesStatus;
    })
    .map((p: any, i: number) => ({ ...p, _rowId: p.id ?? i }));

  const activeAlerts = (alerts ?? []).filter((a: any) => a.is_active !== false);
  const activeMaterials = (materials ?? []).filter((m: any) => m.is_active).length;
  const pendingCount = (procurements ?? []).filter((p: any) => p.status === 'pending').length;

  // ── stock columns ──────────────────────────────────────────────────────────

  const stockColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Material',
      flex: 1.2,
      minWidth: 150,
      renderCell: (p: GridRenderCellParams) => (
        <Box>
          <Typography fontWeight={700} sx={{ color: '#ffffff', fontSize: '0.85rem' }}>
            {p.value}
          </Typography>
          {p.row.description && (
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}>
              {p.row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'current_stock',
      headerName: 'Stock',
      width: 130,
      renderCell: (p: GridRenderCellParams) => {
        const isLow = p.row.is_low_stock;
        return (
          <Typography
            fontWeight={700}
            sx={{
              color: isLow ? '#ffa726' : '#66bb6a',
              fontFamily: 'monospace',
              fontSize: '0.83rem',
            }}
          >
            {parseFloat(p.value).toFixed(1)} {p.row.unit_of_measure}
          </Typography>
        );
      },
    },
    {
      field: 'bags_remaining',
      headerName: 'Bags Left',
      width: 110,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => {
        const kgPerBag = parseFloat(p.row.default_kg_per_bag ?? 0);
        const bags = kgPerBag > 0 ? (parseFloat(p.row.current_stock) / kgPerBag).toFixed(1) : '—';
        return (
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
              {bags !== '—' ? `${bags} bags` : '—'}
            </Typography>
            {kgPerBag > 0 && (
              <Typography sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.65rem' }}>
                {kgPerBag} kg/bag
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'minimum_threshold',
      headerName: 'Min Threshold',
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Typography
          sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', fontFamily: 'monospace' }}
        >
          {parseFloat(p.value).toFixed(0)} {p.row.unit_of_measure}
        </Typography>
      ),
    },
    {
      field: 'last_cost_per_unit',
      headerName: 'Last Cost',
      width: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Typography
          sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', fontFamily: 'monospace' }}
        >
          GH₵ {parseFloat(p.value || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'average_cost_per_unit',
      headerName: 'Avg Cost',
      width: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Typography
          sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', fontFamily: 'monospace' }}
        >
          GH₵ {parseFloat(p.value || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'is_low_stock',
      headerName: 'Stock',
      width: 95,
      renderCell: (p: GridRenderCellParams) =>
        p.value ? (
          <Chip
            label="Low"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: 'rgba(255,152,0,0.15)',
              color: '#ffa726',
              border: '1px solid rgba(255,152,0,0.35)',
            }}
          />
        ) : (
          <Chip
            label="OK"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: 'rgba(76,175,80,0.12)',
              color: '#4caf50',
              border: '1px solid rgba(76,175,80,0.3)',
            }}
          />
        ),
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 85,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={p.value ? 'Active' : 'Off'}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.62rem',
            fontWeight: 700,
            bgcolor: p.value ? 'rgba(76,175,80,0.12)' : 'rgba(158,158,158,0.1)',
            color: p.value ? '#4caf50' : '#9e9e9e',
            border: `1px solid ${p.value ? 'rgba(76,175,80,0.3)' : 'rgba(158,158,158,0.2)'}`,
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 145,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" gap={0.25} height="100%">
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              onClick={() => handleEditMaterial(p.row)}
              sx={{
                color: 'rgba(255,152,0,0.7)',
                '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.1)' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Adjust Stock" arrow>
            <IconButton
              size="small"
              onClick={() => handleAdjustStock(p.row)}
              sx={{
                color: 'rgba(66,165,245,0.7)',
                '&:hover': { color: '#42a5f5', bgcolor: 'rgba(66,165,245,0.1)' },
              }}
            >
              <AdjustIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Stock History" arrow>
            <IconButton
              size="small"
              onClick={() => {
                setHistoryMaterialId(p.row.id);
                setHistoryMaterialName(p.row.name);
              }}
              sx={{
                color: 'rgba(156,39,176,0.7)',
                '&:hover': { color: '#9c27b0', bgcolor: 'rgba(156,39,176,0.1)' },
              }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={p.row.is_active ? 'Deactivate' : 'Activate'} arrow>
            <IconButton
              size="small"
              onClick={() => handleToggleStatus(p.row.id, p.row.is_active)}
              sx={{
                color: p.row.is_active ? 'rgba(244,67,54,0.55)' : 'rgba(76,175,80,0.55)',
                '&:hover': {
                  color: p.row.is_active ? '#f44336' : '#4caf50',
                  bgcolor: p.row.is_active ? 'rgba(244,67,54,0.08)' : 'rgba(76,175,80,0.08)',
                },
              }}
            >
              {p.row.is_active ? (
                <ToggleOnIcon fontSize="small" />
              ) : (
                <ToggleOffIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ── procurement columns ────────────────────────────────────────────────────

  const procurementColumns: GridColDef[] = [
    {
      field: 'procurement_number',
      headerName: 'Ref',
      width: 115,
      renderCell: (p: GridRenderCellParams) => (
        <Typography
          sx={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace', fontSize: '0.75rem' }}
        >
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'raw_material',
      headerName: 'Material',
      flex: 1,
      minWidth: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Typography fontWeight={600} sx={{ color: '#ffffff', fontSize: '0.82rem' }}>
          {p.value?.name ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'bags_purchased',
      headerName: 'Bags',
      width: 80,
      renderCell: (p: GridRenderCellParams) => (
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
          {parseFloat(p.value)}
        </Typography>
      ),
    },
    {
      field: 'total_quantity_kg',
      headerName: 'Total kg',
      width: 100,
      renderCell: (p: GridRenderCellParams) => (
        <Typography fontWeight={600} sx={{ color: '#ff9800', fontSize: '0.8rem' }}>
          {parseFloat(p.value).toFixed(1)} kg
        </Typography>
      ),
    },
    {
      field: 'total_cost',
      headerName: 'Total Cost',
      width: 125,
      renderCell: (p: GridRenderCellParams) => (
        <Typography
          fontWeight={700}
          sx={{ color: '#42a5f5', fontFamily: 'monospace', fontSize: '0.8rem' }}
        >
          GH₵ {parseFloat(p.value).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'procurement_date',
      headerName: 'Date',
      width: 115,
      renderCell: (p: GridRenderCellParams) => (
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>
          {format(new Date(p.value), 'dd MMM yyyy')}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (p: GridRenderCellParams) => {
        const color =
          p.value === 'delivered' ? '#4caf50' : p.value === 'pending' ? '#ff9800' : '#ef5350';
        return (
          <Chip
            label={p.value}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: `${color}18`,
              color,
              border: `1px solid ${color}35`,
              textTransform: 'capitalize',
            }}
          />
        );
      },
    },
    {
      field: 'is_credit',
      headerName: 'Supplier / Type',
      flex: 1,
      minWidth: 130,
      renderCell: (p: GridRenderCellParams) =>
        p.value ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
              {p.row.supplier?.name ?? '—'}
            </Typography>
            <Chip
              label="Credit"
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                bgcolor: 'rgba(33,150,243,0.12)',
                color: '#42a5f5',
                border: '1px solid rgba(33,150,243,0.3)',
              }}
            />
          </Box>
        ) : (
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem' }}>Cash</Typography>
        ),
    },
    {
      field: 'proc_actions',
      headerName: '',
      width: 108,
      sortable: false,
      renderCell: (p: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" gap={0.25} height="100%">
          <Tooltip title={p.row.status === 'delivered' ? 'Change Supplier' : 'Edit'} arrow>
            <IconButton
              size="small"
              onClick={() => handleOpenEditProcurement(p.row)}
              sx={{
                color: 'rgba(255,152,0,0.6)',
                '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.08)' },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.status === 'pending' && (
            <Tooltip title="Mark Delivered" arrow>
              <IconButton
                size="small"
                onClick={() => handleMarkDelivered(p.row.id)}
                sx={{
                  color: 'rgba(76,175,80,0.7)',
                  '&:hover': { color: '#4caf50', bgcolor: 'rgba(76,175,80,0.1)' },
                }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              onClick={() => handleDeleteProcurement(p.row.id)}
              sx={{
                color: 'rgba(244,67,54,0.6)',
                '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.08)' },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // ── bulk procurement totals ───────────────────────────────────────────────

  const grandTotalCost = procureLines.reduce((sum, l) => {
    const bags = parseFloat(l.bags_purchased) || 0;
    const cost = parseFloat(l.cost_per_bag) || 0;
    return sum + bags * cost;
  }, 0);
  const grandTotalKg = procureLines.reduce((sum, l) => {
    const bags = parseFloat(l.bags_purchased) || 0;
    const kg = parseFloat(l.kg_per_bag) || 50;
    return sum + bags * kg;
  }, 0);

  const adjDelta =
    editingMaterial && adjustStockFormData.new_quantity
      ? parseFloat(adjustStockFormData.new_quantity) -
        parseFloat(editingMaterial.current_stock ?? 0)
      : null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(isFetching || isMutating) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffcc02 50%, #ff9800 100%)',
            },
          }}
        />
      )}

      {/* ── Header ── */}
      <Box
        sx={{
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0e0e10 0%, #1a1a1d 60%, #0e0e10 100%)',
          border: '1px solid rgba(255,152,0,0.12)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -60,
            right: -60,
            width: 220,
            height: 220,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,152,0,0.06) 0%, transparent 65%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            p: { xs: 2.5, md: 3 },
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            position: 'relative',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2.5,
              flexShrink: 0,
              background:
                'linear-gradient(135deg, rgba(255,152,0,0.18) 0%, rgba(255,152,0,0.06) 100%)',
              border: '1px solid rgba(255,152,0,0.22)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ScienceIcon sx={{ color: '#ff9800', fontSize: 28 }} />
          </Box>
          <Box flex={1}>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                color: '#ffffff',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                fontSize: { xs: '1.5rem', md: '1.9rem' },
              }}
            >
              Raw Materials
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.38)', mt: 0.4 }}>
              Manage feed ingredients, stock levels and procurement orders
            </Typography>
          </Box>
          <Box display="flex" gap={1.5} flexShrink={0}>
            <Button
              startIcon={<ShoppingCartIcon />}
              onClick={() => setProcureDialogOpen(true)}
              variant="outlined"
              size="small"
              sx={{
                color: '#ff9800',
                borderColor: 'rgba(255,152,0,0.4)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { borderColor: '#ff9800', bgcolor: 'rgba(255,152,0,0.06)' },
              }}
            >
              Procure
            </Button>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                borderRadius: 2,
                px: 2.5,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.875rem',
                boxShadow: '0 4px 18px rgba(255,152,0,0.32)',
                '&:hover': { background: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)' },
              }}
            >
              New Material
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Stats Strip ── */}
      <Box display="flex" gap={2} mb={4} flexWrap="wrap">
        {[
          {
            label: 'Total Materials',
            value: (materials ?? []).length,
            color: '#ff9800',
            Icon: CategoryIcon,
          },
          {
            label: 'Active Materials',
            value: activeMaterials,
            color: '#66bb6a',
            Icon: InventoryIcon,
          },
          {
            label: 'Low Stock',
            value: activeAlerts.length,
            color: activeAlerts.length > 0 ? '#ffa726' : '#66bb6a',
            Icon: WarningIcon,
          },
          {
            label: 'Pending Orders',
            value: pendingCount,
            color: pendingCount > 0 ? '#42a5f5' : 'rgba(255,255,255,0.3)',
            Icon: ShippingIcon,
          },
        ].map(({ label, value, color, Icon }) => (
          <Paper
            key={label}
            elevation={0}
            sx={{
              flex: 1,
              minWidth: 160,
              p: 2.5,
              background: '#111318',
              border: '1px solid rgba(255,255,255,0.05)',
              borderLeft: `3px solid ${color}`,
              borderRadius: 2.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.18s, box-shadow 0.18s',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 36px rgba(0,0,0,0.55), 0 0 0 1px ${color}22`,
              },
            }}
          >
            <Icon
              sx={{
                position: 'absolute',
                right: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 56,
                color: `${color}0D`,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.35)',
                letterSpacing: '0.7px',
                fontSize: '0.62rem',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.75,
              }}
            >
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1 }}>
              {value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* ── Low stock alerts ── */}
      {activeAlerts.length > 0 && (
        <Alert
          icon={<WarningIcon />}
          severity="warning"
          sx={{
            mb: 3,
            borderRadius: 2,
            bgcolor: 'rgba(255,152,0,0.07)',
            border: '1px solid rgba(255,152,0,0.22)',
            color: '#ffffff',
            '& .MuiAlert-icon': { color: '#ffa726' },
          }}
        >
          <Typography fontWeight={700} sx={{ fontSize: '0.82rem', mb: 0.5 }}>
            {activeAlerts.length} material{activeAlerts.length > 1 ? 's' : ''} running low
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.75} mt={0.5}>
            {activeAlerts.map((a: any) => (
              <Chip
                key={a.id}
                label={`${a.name} — ${parseFloat(a.current_stock).toFixed(0)} ${a.unit_of_measure}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(255,152,0,0.15)',
                  color: '#ffa726',
                  border: '1px solid rgba(255,152,0,0.3)',
                }}
              />
            ))}
          </Box>
        </Alert>
      )}

      {/* ── Tabs Panel ── */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid rgba(255,152,0,0.12)',
          borderRadius: 2.5,
          overflow: 'hidden',
          background: '#0e0e10',
        }}
      >
        <Box
          sx={{
            borderBottom: '1px solid rgba(255,152,0,0.1)',
            px: 2,
            background: 'linear-gradient(90deg, rgba(255,152,0,0.05) 0%, transparent 100%)',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.4)',
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48,
                fontSize: '0.82rem',
              },
              '& .Mui-selected': { color: '#ff9800' },
              '& .MuiTabs-indicator': { backgroundColor: '#ff9800', height: 2 },
            }}
          >
            <Tab
              icon={<InventoryIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label={`Stock Levels (${(materials ?? []).length})`}
            />
            <Tab
              icon={<ShoppingCartIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label={`Procurement History (${(procurements ?? []).length})`}
            />
          </Tabs>
        </Box>

        {/* Stock Levels */}
        {tab === 0 && (
          <Box>
            <Box
              display="flex"
              gap={2}
              p={2}
              sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
              flexWrap="wrap"
            >
              <TextField
                label="Search material"
                size="small"
                value={stockFilters.materialName}
                onChange={(e) => setStockFilters({ ...stockFilters, materialName: e.target.value })}
                placeholder="Filter by name…"
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' },
                }}
              />
              <TextField
                select
                label="Stock Status"
                size="small"
                value={stockFilters.stockStatus}
                onChange={(e) => setStockFilters({ ...stockFilters, stockStatus: e.target.value })}
                sx={{
                  minWidth: 140,
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' },
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="low">Low Stock</MenuItem>
                <MenuItem value="ok">OK</MenuItem>
              </TextField>
              <TextField
                select
                label="Active"
                size="small"
                value={stockFilters.activeStatus}
                onChange={(e) => setStockFilters({ ...stockFilters, activeStatus: e.target.value })}
                sx={{
                  minWidth: 130,
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' },
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Box>
            {materialsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} sx={{ color: '#ff9800' }} />
              </Box>
            ) : (
              <DataGrid
                rows={filteredMaterials}
                columns={stockColumns}
                initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
                pageSizeOptions={[10, 15, 25]}
                disableRowSelectionOnClick
                autoHeight
                rowHeight={58}
                sx={dataGridSx}
              />
            )}
          </Box>
        )}

        {/* Procurement History */}
        {tab === 1 && (
          <Box>
            <Box
              display="flex"
              gap={2}
              p={2}
              sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
              flexWrap="wrap"
            >
              <TextField
                label="Search material"
                size="small"
                value={procurementFilters.materialName}
                onChange={(e) =>
                  setProcurementFilters({ ...procurementFilters, materialName: e.target.value })
                }
                placeholder="Filter by name…"
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' },
                }}
              />
              <TextField
                select
                label="Status"
                size="small"
                value={procurementFilters.status}
                onChange={(e) =>
                  setProcurementFilters({ ...procurementFilters, status: e.target.value })
                }
                sx={{
                  minWidth: 140,
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' },
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Box>
            {procurementsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} sx={{ color: '#ff9800' }} />
              </Box>
            ) : (
              <DataGrid
                rows={filteredProcurements}
                getRowId={(r) => r._rowId}
                columns={procurementColumns}
                initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
                pageSizeOptions={[10, 15, 25, 50]}
                disableRowSelectionOnClick
                autoHeight
                rowHeight={52}
                sx={dataGridSx}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* ══ Procure Dialog (bulk) ══ */}
      <Dialog
        open={procureDialogOpen}
        onClose={() => setProcureDialogOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <DialogHeader
          title="Procure Raw Materials"
          subtitle={`${procureLines.length} item${procureLines.length !== 1 ? 's' : ''}`}
        />
        <DialogContent sx={{ pt: 2 }}>
          {/* Shared dates */}
          <Grid container spacing={2} mb={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Procurement Date"
                type="date"
                size="small"
                fullWidth
                required
                value={procureShared.procurement_date}
                onChange={(e) =>
                  setProcureShared((s) => ({ ...s, procurement_date: e.target.value }))
                }
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Delivery Date (optional)"
                type="date"
                size="small"
                fullWidth
                value={procureShared.delivery_date}
                onChange={(e) => setProcureShared((s) => ({ ...s, delivery_date: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 2 }} />

          {/* Line items */}
          <Box display="flex" flexDirection="column" gap={2}>
            {procureLines.map((line, idx) => (
              <Paper
                key={idx}
                elevation={0}
                sx={{
                  p: 2,
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 2,
                  position: 'relative',
                }}
              >
                {/* Row number + delete */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.35)',
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Item {idx + 1}
                  </Typography>
                  {procureLines.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => setProcureLines((lines) => lines.filter((_, i) => i !== idx))}
                      sx={{ color: 'rgba(239,83,80,0.6)', '&:hover': { color: '#ef5350' }, p: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Grid container spacing={1.5}>
                  {/* Row 1: Material (full width) */}
                  <Grid size={{ xs: 12 }}>
                    <Autocomplete
                      options={(materials ?? []).map((m: any) => ({
                        label: m.name,
                        id: m.id,
                        default_kg_per_bag: m.default_kg_per_bag,
                      }))}
                      value={
                        materials?.find((m: any) => m.id === line.raw_material_id)
                          ? {
                              label:
                                materials.find((m: any) => m.id === line.raw_material_id)?.name ||
                                '',
                              id: line.raw_material_id,
                              default_kg_per_bag: '',
                            }
                          : null
                      }
                      onChange={(_, val: any) => {
                        const sel = materials?.find((m: any) => m.id === val?.id);
                        updateLine(idx, {
                          raw_material_id: val ? val.id : 0,
                          kg_per_bag: sel?.default_kg_per_bag?.toString() || '50',
                        });
                      }}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Raw Material *"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Row 2: Bags | Cost/Bag | kg/bag toggle */}
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Bags *"
                      type="number"
                      size="small"
                      fullWidth
                      value={line.bags_purchased}
                      onChange={(e) => updateLine(idx, { bags_purchased: e.target.value })}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>

                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Cost/Bag (GH₵) *"
                      type="number"
                      size="small"
                      fullWidth
                      value={line.cost_per_bag}
                      onChange={(e) => updateLine(idx, { cost_per_bag: e.target.value })}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>

                  {/* kg/bag */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box display="flex" gap={0.75} alignItems="stretch">
                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.35)',
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap',
                          alignSelf: 'center',
                          minWidth: 'max-content',
                        }}
                      >
                        kg/bag
                      </Typography>
                      {['25', '50'].map((v) => (
                        <Button
                          key={v}
                          size="small"
                          onClick={() => updateLine(idx, { kg_per_bag: v })}
                          sx={{
                            minWidth: 56,
                            flex: 1,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 1.5,
                            fontSize: '0.82rem',
                            py: 0.9,
                            ...(line.kg_per_bag === v
                              ? {
                                  background: 'linear-gradient(135deg,#ff9800,#f57c00)',
                                  color: '#fff',
                                  border: 'none',
                                }
                              : {
                                  color: '#ff9800',
                                  border: '1px solid rgba(255,152,0,0.35)',
                                  '&:hover': {
                                    borderColor: '#ff9800',
                                    bgcolor: 'rgba(255,152,0,0.06)',
                                  },
                                }),
                          }}
                        >
                          {v}kg
                        </Button>
                      ))}
                      <TextField
                        placeholder="custom"
                        type="number"
                        size="small"
                        value={!['25', '50'].includes(line.kg_per_bag) ? line.kg_per_bag : ''}
                        onChange={(e) => updateLine(idx, { kg_per_bag: e.target.value })}
                        sx={{ flex: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        slotProps={{ htmlInput: { min: 1 } }}
                      />
                    </Box>
                  </Grid>

                  {/* Row 3: Supplier */}
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Box display="flex" gap={0.5} alignItems="center">
                      <Autocomplete
                        options={suppliers.filter((s: any) => s.is_active)}
                        getOptionLabel={(s: any) => s.name}
                        value={suppliers.find((s: any) => s.id === line.supplier_id) ?? null}
                        onChange={(_, val: any) =>
                          updateLine(idx, {
                            supplier_id: val ? val.id : null,
                            is_credit: val ? line.is_credit : false,
                          })
                        }
                        isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                        sx={{ flex: 1 }}
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={line.is_credit ? 'Supplier *' : 'Supplier (optional)'}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />
                        )}
                      />
                      <Tooltip title="Add new supplier">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setQuickAddSupplierName('');
                            setQuickAddSupplierDialogOpen(true);
                          }}
                          sx={{
                            bgcolor: 'rgba(255,152,0,0.08)',
                            border: '1px solid rgba(255,152,0,0.2)',
                            '&:hover': { bgcolor: 'rgba(255,152,0,0.15)' },
                          }}
                        >
                          <AddIcon sx={{ color: '#ff9800', fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>

                  {/* Credit toggle */}
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Box display="flex" alignItems="center" gap={1} height="100%" pl={0.5}>
                      <Switch
                        size="small"
                        checked={line.is_credit}
                        onChange={(e) => updateLine(idx, { is_credit: e.target.checked })}
                        color="warning"
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: line.is_credit ? '#ff9800' : 'rgba(255,255,255,0.4)',
                          fontSize: '0.8rem',
                        }}
                      >
                        Credit
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Notes */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Notes"
                      size="small"
                      fullWidth
                      value={line.notes}
                      onChange={(e) => updateLine(idx, { notes: e.target.value })}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                </Grid>

                {/* Line totals */}
                {line.bags_purchased && line.cost_per_bag && (
                  <Box display="flex" gap={3} mt={1.5} pl={0.5}>
                    <Typography
                      sx={{
                        color: '#ff9800',
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                        fontWeight: 700,
                      }}
                    >
                      {(
                        parseFloat(line.bags_purchased) * (parseFloat(line.kg_per_bag) || 50)
                      ).toFixed(0)}{' '}
                      kg
                    </Typography>
                    <Typography
                      sx={{
                        color: '#42a5f5',
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                        fontWeight: 700,
                      }}
                    >
                      GH₵{' '}
                      {(parseFloat(line.bags_purchased) * parseFloat(line.cost_per_bag)).toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>

          {/* Add item button */}
          <Button
            startIcon={<AddIcon />}
            onClick={() => setProcureLines((lines) => [...lines, emptyLine()])}
            sx={{
              mt: 1.5,
              color: '#ff9800',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(255,152,0,0.06)' },
            }}
          >
            Add another material
          </Button>

          {/* Grand total */}
          {grandTotalCost > 0 && (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 2,
                background: 'rgba(255,152,0,0.05)',
                border: '1px solid rgba(255,152,0,0.15)',
                borderRadius: 2,
                display: 'flex',
                gap: 4,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.35)',
                    fontSize: '0.6rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Total kg
                </Typography>
                <Typography
                  fontWeight={800}
                  sx={{ color: '#ff9800', fontFamily: 'monospace', fontSize: '1.1rem' }}
                >
                  {grandTotalKg.toFixed(0)} kg
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.35)',
                    fontSize: '0.6rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Grand Total
                </Typography>
                <Typography
                  fontWeight={800}
                  sx={{ color: '#42a5f5', fontFamily: 'monospace', fontSize: '1.1rem' }}
                >
                  GH₵ {grandTotalCost.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setProcureDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcure}
            variant="contained"
            disabled={
              procureLines.every(
                (l) => !l.raw_material_id || !l.bags_purchased || !l.cost_per_bag
              ) ||
              procureLines.some((l) => l.is_credit && !l.supplier_id) ||
              bulkProcureMutation.isPending
            }
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {bulkProcureMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              `Procure ${procureLines.filter((l) => l.raw_material_id && l.bags_purchased && l.cost_per_bag).length} item${procureLines.filter((l) => l.raw_material_id && l.bags_purchased && l.cost_per_bag).length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Create Material Dialog ══ */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <DialogHeader title="New Raw Material" />
        <DialogContent sx={{ pt: 3 }}>
          <Box display="flex" flexDirection="column" gap={2.5}>
            <TextField
              label="Material Name"
              size="small"
              fullWidth
              required
              value={materialFormData.name}
              onChange={(e) => setMaterialFormData({ ...materialFormData, name: e.target.value })}
              placeholder="e.g., Corn, Soybean, Premix"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <TextField
              label="Description"
              multiline
              rows={2}
              size="small"
              fullWidth
              value={materialFormData.description}
              onChange={(e) =>
                setMaterialFormData({ ...materialFormData, description: e.target.value })
              }
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Unit of Measure"
                  size="small"
                  fullWidth
                  required
                  value={materialFormData.unit_of_measure}
                  onChange={(e) =>
                    setMaterialFormData({ ...materialFormData, unit_of_measure: e.target.value })
                  }
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Default kg/bag"
                  type="number"
                  size="small"
                  fullWidth
                  required
                  value={materialFormData.default_kg_per_bag}
                  onChange={(e) =>
                    setMaterialFormData({ ...materialFormData, default_kg_per_bag: e.target.value })
                  }
                  helperText="Typically 25 or 50"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Min Threshold (kg)"
                  type="number"
                  size="small"
                  fullWidth
                  required
                  value={materialFormData.minimum_threshold}
                  onChange={(e) =>
                    setMaterialFormData({ ...materialFormData, minimum_threshold: e.target.value })
                  }
                  helperText="Alert below this"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateMaterial}
            variant="contained"
            disabled={
              !materialFormData.name ||
              !materialFormData.default_kg_per_bag ||
              !materialFormData.minimum_threshold ||
              createMutation.isPending
            }
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {createMutation.isPending ? <CircularProgress size={18} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Edit Material Dialog ══ */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <DialogHeader title="Edit Material" subtitle={editingMaterial?.name} />
        <DialogContent sx={{ pt: 3 }}>
          <Box display="flex" flexDirection="column" gap={2.5}>
            <TextField
              label="Material Name"
              size="small"
              fullWidth
              value={editMaterialFormData.name}
              onChange={(e) =>
                setEditMaterialFormData({ ...editMaterialFormData, name: e.target.value })
              }
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <TextField
              label="Description"
              multiline
              rows={2}
              size="small"
              fullWidth
              value={editMaterialFormData.description}
              onChange={(e) =>
                setEditMaterialFormData({ ...editMaterialFormData, description: e.target.value })
              }
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Unit of Measure"
                  size="small"
                  fullWidth
                  value={editMaterialFormData.unit_of_measure}
                  onChange={(e) =>
                    setEditMaterialFormData({
                      ...editMaterialFormData,
                      unit_of_measure: e.target.value,
                    })
                  }
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Default kg/bag"
                  type="number"
                  size="small"
                  fullWidth
                  value={editMaterialFormData.default_kg_per_bag}
                  onChange={(e) =>
                    setEditMaterialFormData({
                      ...editMaterialFormData,
                      default_kg_per_bag: e.target.value,
                    })
                  }
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Min Threshold (kg)"
                  type="number"
                  size="small"
                  fullWidth
                  value={editMaterialFormData.minimum_threshold}
                  onChange={(e) =>
                    setEditMaterialFormData({
                      ...editMaterialFormData,
                      minimum_threshold: e.target.value,
                    })
                  }
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Current Stock (kg)"
              type="number"
              size="small"
              fullWidth
              value={editMaterialFormData.current_stock}
              onChange={(e) =>
                setEditMaterialFormData({ ...editMaterialFormData, current_stock: e.target.value })
              }
              helperText="Manually override current stock level"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            <TextField
              label="Top-up per Bag (kg)"
              type="number"
              size="small"
              fullWidth
              value={editMaterialFormData.top_up_kg}
              onChange={(e) =>
                setEditMaterialFormData({ ...editMaterialFormData, top_up_kg: e.target.value })
              }
              placeholder="e.g. 0.3"
              helperText="Auto-added per bag during feed production to account for weight variance. Leave blank to disable."
              slotProps={{ htmlInput: { step: '0.001', min: '0' } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateMaterial}
            variant="contained"
            disabled={updateMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {updateMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Adjust Stock Dialog ══ */}
      <Dialog
        open={adjustStockDialogOpen}
        onClose={() => setAdjustStockDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <DialogHeader title="Adjust Stock" subtitle={editingMaterial?.name} />
        <DialogContent sx={{ pt: 3 }}>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {editingMaterial && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: 'rgba(255,152,0,0.05)',
                  border: '1px solid rgba(255,152,0,0.12)',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.35)',
                    fontSize: '0.62rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block',
                  }}
                >
                  Current Stock
                </Typography>
                <Typography
                  fontWeight={800}
                  sx={{ color: '#ff9800', fontFamily: 'monospace', fontSize: '1.1rem' }}
                >
                  {parseFloat(editingMaterial.current_stock ?? 0).toFixed(2)}{' '}
                  {editingMaterial.unit_of_measure}
                </Typography>
              </Paper>
            )}
            <TextField
              label={`New quantity (${editingMaterial?.unit_of_measure || 'kg'})`}
              type="number"
              size="small"
              fullWidth
              required
              value={adjustStockFormData.new_quantity}
              onChange={(e) =>
                setAdjustStockFormData({ ...adjustStockFormData, new_quantity: e.target.value })
              }
              slotProps={{ htmlInput: { step: '0.01', min: '0' } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
            {adjDelta !== null && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  background:
                    adjDelta > 0
                      ? 'rgba(76,175,80,0.08)'
                      : adjDelta < 0
                        ? 'rgba(244,67,54,0.08)'
                        : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${adjDelta > 0 ? 'rgba(76,175,80,0.25)' : adjDelta < 0 ? 'rgba(244,67,54,0.25)' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem' }}
                >
                  Change
                </Typography>
                <Typography
                  fontWeight={800}
                  sx={{
                    color: adjDelta > 0 ? '#4caf50' : adjDelta < 0 ? '#ef5350' : '#9e9e9e',
                    fontFamily: 'monospace',
                  }}
                >
                  {adjDelta > 0 ? '+' : ''}
                  {adjDelta.toFixed(2)} {editingMaterial?.unit_of_measure}
                </Typography>
              </Paper>
            )}
            <TextField
              label="Reason (optional)"
              multiline
              rows={2}
              size="small"
              fullWidth
              value={adjustStockFormData.reason}
              onChange={(e) =>
                setAdjustStockFormData({ ...adjustStockFormData, reason: e.target.value })
              }
              placeholder="e.g., Physical count correction"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setAdjustStockDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitStockAdjustment}
            variant="contained"
            disabled={
              !adjustStockFormData.new_quantity ||
              parseFloat(adjustStockFormData.new_quantity) < 0 ||
              adjustStockMutation.isPending
            }
            sx={{
              background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {adjustStockMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Adjust Stock'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Edit Procurement Dialog ══ */}
      <Dialog
        open={editProcurementDialogOpen}
        onClose={() => setEditProcurementDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <DialogHeader title="Edit Procurement" subtitle={editingProcurement?.procurement_number} />
        <DialogContent sx={{ pt: 3 }}>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {editingProcurement && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  background: 'rgba(255,152,0,0.04)',
                  border: '1px solid rgba(255,152,0,0.12)',
                  borderRadius: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.7rem' }}
                >
                  {editingProcurement.raw_material?.name}
                </Typography>
              </Paper>
            )}

            {editingProcurement?.status !== 'delivered' && (
              <>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Bags Purchased"
                      type="number"
                      size="small"
                      fullWidth
                      value={editProcurementForm.bags_purchased}
                      onChange={(e) =>
                        setEditProcurementForm({
                          ...editProcurementForm,
                          bags_purchased: e.target.value,
                        })
                      }
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="kg / Bag"
                      type="number"
                      size="small"
                      fullWidth
                      value={editProcurementForm.kg_per_bag}
                      onChange={(e) =>
                        setEditProcurementForm({
                          ...editProcurementForm,
                          kg_per_bag: e.target.value,
                        })
                      }
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Cost / Bag (GH₵)"
                      type="number"
                      size="small"
                      fullWidth
                      value={editProcurementForm.cost_per_bag}
                      onChange={(e) =>
                        setEditProcurementForm({
                          ...editProcurementForm,
                          cost_per_bag: e.target.value,
                        })
                      }
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  </Grid>
                </Grid>

                {editProcurementForm.bags_purchased &&
                  editProcurementForm.kg_per_bag &&
                  editProcurementForm.cost_per_bag && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: 1.5,
                        display: 'flex',
                        gap: 3,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                        >
                          Total kg
                        </Typography>
                        <Typography
                          fontWeight={700}
                          sx={{ color: '#ff9800', fontFamily: 'monospace', fontSize: '0.82rem' }}
                        >
                          {(
                            parseFloat(editProcurementForm.bags_purchased) *
                            parseFloat(editProcurementForm.kg_per_bag)
                          ).toFixed(1)}{' '}
                          kg
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                        >
                          Total Cost
                        </Typography>
                        <Typography
                          fontWeight={700}
                          sx={{ color: '#42a5f5', fontFamily: 'monospace', fontSize: '0.82rem' }}
                        >
                          GH₵{' '}
                          {(
                            parseFloat(editProcurementForm.bags_purchased) *
                            parseFloat(editProcurementForm.cost_per_bag)
                          ).toFixed(2)}
                        </Typography>
                      </Box>
                    </Paper>
                  )}
              </>
            )}

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography
                  sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 600 }}
                >
                  Credit Purchase
                </Typography>
                {editingProcurement?.status === 'delivered' && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                    Changing supplier reassigns the amount owed
                  </Typography>
                )}
              </Box>
              <Switch
                checked={editProcurementForm.is_credit}
                disabled={editingProcurement?.status === 'delivered'}
                onChange={(e) =>
                  setEditProcurementForm({
                    ...editProcurementForm,
                    is_credit: e.target.checked,
                    supplier_id: e.target.checked ? editProcurementForm.supplier_id : null,
                  })
                }
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff9800' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#ff9800',
                  },
                }}
              />
            </Box>

            {editProcurementForm.is_credit && (
              <Autocomplete
                options={(suppliers ?? []).map((s: any) => ({ label: s.name, id: s.id }))}
                value={
                  suppliers?.find((s: any) => s.id === editProcurementForm.supplier_id)
                    ? {
                        label:
                          suppliers.find((s: any) => s.id === editProcurementForm.supplier_id)
                            ?.name || '',
                        id: editProcurementForm.supplier_id as number,
                      }
                    : null
                }
                onChange={(_: any, val: any) =>
                  setEditProcurementForm({ ...editProcurementForm, supplier_id: val?.id ?? null })
                }
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Supplier"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                )}
              />
            )}

            <TextField
              label="Notes"
              multiline
              rows={3}
              size="small"
              fullWidth
              value={editProcurementForm.notes}
              onChange={(e) =>
                setEditProcurementForm({ ...editProcurementForm, notes: e.target.value })
              }
              placeholder="Optional notes…"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setEditProcurementDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateProcurement}
            variant="contained"
            disabled={updateProcurementMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {updateProcurementMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Quick-Add Supplier Dialog ══ */}
      <Dialog
        open={quickAddSupplierDialogOpen}
        onClose={() => setQuickAddSupplierDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: dialogPaper } }}
      >
        <DialogHeader title="Add Supplier" />
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            label="Supplier Name"
            value={quickAddSupplierName}
            onChange={(e) => setQuickAddSupplierName(e.target.value)}
            fullWidth
            required
            autoFocus
            size="small"
            helperText="You can add phone and categories later"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setQuickAddSupplierDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!quickAddSupplierName || createSupplierMutation.isPending}
            onClick={() =>
              handleQuickAddSupplier(quickAddSupplierName, (s) =>
                setFormData((p) => ({ ...p, supplier_id: s.id }))
              )
            }
            sx={{
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {createSupplierMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Add & Select'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Stock History Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={!!historyMaterialId}
        onClose={() => setHistoryMaterialId(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogHeader title="Stock History" subtitle={historyMaterialName} />
        <DialogContent sx={{ p: 0 }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress size={32} sx={{ color: '#ff9800' }} />
            </Box>
          ) : !historyData?.history?.length ? (
            <Box textAlign="center" py={6}>
              <HistoryIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.1)', mb: 1 }} />
              <Typography color="rgba(255,255,255,0.35)" fontSize="0.9rem">
                No stock movement records yet
              </Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 520, overflow: 'auto', px: 3, py: 2 }}>
              {(historyData.history as any[]).map((row, idx) => {
                const isIncrease = parseFloat(row.quantity_change) >= 0;
                const absChange = Math.abs(parseFloat(row.quantity_change));
                const eventColor =
                  row.event_type === 'procurement'
                    ? '#4caf50'
                    : row.event_type === 'feed_production'
                      ? '#f44336'
                      : '#ff9800';
                const eventLabel =
                  row.event_type === 'procurement'
                    ? 'Procurement'
                    : row.event_type === 'feed_production'
                      ? 'Feed Production'
                      : 'Manual Adjustment';

                return (
                  <Box key={idx}>
                    {idx > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 0 }} />}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      py={1.5}
                      sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }, borderRadius: 1 }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: `${eventColor}18`,
                          border: `1px solid ${eventColor}33`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {isIncrease ? (
                          <TrendingUpIcon sx={{ fontSize: 18, color: eventColor }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 18, color: eventColor }} />
                        )}
                      </Box>

                      {/* Main content */}
                      <Box flex={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            label={eventLabel}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.62rem',
                              fontWeight: 700,
                              bgcolor: `${eventColor}18`,
                              color: eventColor,
                              border: `1px solid ${eventColor}33`,
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.72rem',
                              color: 'rgba(255,255,255,0.35)',
                            }}
                          >
                            {row.reference}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={2} mt={0.3} flexWrap="wrap">
                          {row.supplier_name && (
                            <Typography fontSize="0.78rem" color="rgba(255,255,255,0.55)">
                              {row.event_type === 'feed_production' ? 'Batch: ' : 'Supplier: '}
                              <span style={{ color: 'rgba(255,255,255,0.8)' }}>
                                {row.supplier_name}
                              </span>
                            </Typography>
                          )}
                          {row.notes && (
                            <Typography fontSize="0.78rem" color="rgba(255,255,255,0.4)">
                              {row.notes}
                            </Typography>
                          )}
                          {row.performed_by_name && (
                            <Typography fontSize="0.78rem" color="rgba(255,255,255,0.3)">
                              by {row.performed_by_name}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Right side: change + date */}
                      <Box textAlign="right" flexShrink={0}>
                        <Typography
                          fontWeight={700}
                          fontSize="0.95rem"
                          sx={{ color: isIncrease ? '#4caf50' : '#f44336' }}
                        >
                          {isIncrease ? '+' : '-'}
                          {absChange.toFixed(2)}{' '}
                          <span style={{ fontSize: '0.7rem', fontWeight: 400 }}>
                            {historyData.material?.unit_of_measure ?? 'kg'}
                          </span>
                        </Typography>
                        <Typography fontSize="0.72rem" color="rgba(255,255,255,0.3)" mt={0.2}>
                          {row.event_date
                            ? format(new Date(row.event_date + 'T00:00:00'), 'dd MMM yyyy')
                            : '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setHistoryMaterialId(null)}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RawMaterialsPage;
